import { useState } from "react";
import { Link } from "react-router";
import { marketFiltersToQuery } from "../../../shared/market";
import { useI18n } from "../../i18n/I18nProvider";
import { imageUrl } from "../../lib/api";
import { useGame } from "../../lib/GameProvider";
import { useAsync } from "../../lib/useAsync";
import { marketApi } from "../../market/api";
import { ListingTile, offersLink } from "../../market/components";

/**
 * The home's market sections, of the visitor's game: the latest cards on sale, every set with
 * something on sale, and the sealed products by kind. Each one hides when it has nothing to show (or
 * can't load): the home never fills with empty boxes or retry buttons.
 */

const STRIP_SIZE = 12;
const SETS_SHOWN = 15;

/** Placeholders while a strip loads, so the page doesn't jump when it arrives. */
function StripSkeleton() {
  return (
    <div className="home-strip" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="product-card skeleton" />
      ))}
    </div>
  );
}

/** "Recién llegado": the cards most recently put on sale (no sealed products). */
export function NewArrivals() {
  const { t } = useI18n();
  const { game } = useGame();
  const filters = { categories: ["singles"], sort: "newest" as const, game };
  const result = useAsync((signal) => marketApi.listings(filters, signal), [game]);
  const items = result.data?.items.slice(0, STRIP_SIZE) ?? [];
  const loading = result.loading && !result.data;
  if (result.error || (!loading && !items.length)) return null;
  return (
    <section className="container section" aria-labelledby="home-new">
      <div className="section-head">
        <h2 className="section-title" id="home-new">
          {t("home.newArrivals")}
        </h2>
        <Link to={`/mercado?${marketFiltersToQuery({ categories: ["singles"] })}`}>{t("home.newArrivalsAll")} →</Link>
      </div>
      {loading ? (
        <StripSkeleton />
      ) : (
        <div className="home-strip">
          {items.map((l) => (
            <ListingTile key={l.id} listing={l} groupLink={offersLink} />
          ))}
        </div>
      )}
    </section>
  );
}

/** "Más colecciones": every set with something on sale, newest first; each opens the market filtered by it. */
export function SetGrid() {
  const { t } = useI18n();
  const { game } = useGame();
  const [all, setAll] = useState(false);
  const sets = useAsync((signal) => marketApi.sets(game, signal), [game]);
  const list = sets.data ?? [];
  const loading = sets.loading && !sets.data;
  if (sets.error || (!loading && !list.length)) return null;
  return (
    <section className="container section" aria-labelledby="home-sets">
      <h2 className="section-title" id="home-sets">
        {t("home.sets")}
      </h2>
      <ul className="home-sets" aria-busy={loading}>
        {loading && Array.from({ length: 10 }, (_, i) => <li key={i} className="home-set skeleton" aria-hidden="true" />)}
        {(all ? list : list.slice(0, SETS_SHOWN)).map((s) => (
          <li key={s.name}>
            <Link to={`/mercado?${marketFiltersToQuery({ sets: [s.name] })}`} className="home-set">
              <img src={imageUrl(s.imageKey)} alt="" loading="lazy" width={240} height={335} />
              <span className="home-set-name">{s.name}</span>
              <span className="home-set-count">{t("home.setsCount", { count: s.products })}</span>
            </Link>
          </li>
        ))}
      </ul>
      {!all && list.length > SETS_SHOWN && (
        <button type="button" className="btn btn-outline home-more" onClick={() => setAll(true)}>
          {t("home.setsAll", { count: list.length })}
        </button>
      )}
    </section>
  );
}

const SEALED_TABS = [
  { id: "packs", categories: ["sobres"] },
  { id: "boxes", categories: ["booster-box", "colecciones", "mazos"] },
  { id: "etb", categories: ["etb"] },
] as const;
type SealedTab = (typeof SEALED_TABS)[number]["id"];

/** "Sellados", in three tabs: packs; boxes, collections and decks; Elite Trainer Boxes. Only the open tab loads. */
export function SealedTabs() {
  const { t } = useI18n();
  const { game } = useGame();
  const [tab, setTab] = useState<SealedTab>("packs");
  const categories = [...SEALED_TABS.find((x) => x.id === tab)!.categories];
  const result = useAsync((signal) => marketApi.listings({ categories, sort: "newest", game }, signal), [tab, game]);
  if (result.error) return null;
  const items = result.data?.items.slice(0, STRIP_SIZE) ?? [];
  return (
    <section className="container section" aria-labelledby="home-sealed">
      <div className="section-head">
        <h2 className="section-title" id="home-sealed">
          {t("home.sealed")}
        </h2>
        <Link to={`/mercado?${marketFiltersToQuery({ categories })}`}>{t("home.sealedAll")} →</Link>
      </div>
      <div className="tabs" role="tablist" aria-label={t("home.sealed")}>
        {SEALED_TABS.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            id={`home-sealed-${x.id}`}
            aria-selected={tab === x.id}
            aria-controls="home-sealed-panel"
            className={tab === x.id ? "tab is-active" : "tab"}
            onClick={() => setTab(x.id)}
          >
            {t(`home.sealed.${x.id}`)}
          </button>
        ))}
      </div>
      <div id="home-sealed-panel" role="tabpanel" aria-labelledby={`home-sealed-${tab}`}>
        {/* While a tab loads, the previous tab's items must not show under its name. */}
        {result.loading ? (
          <StripSkeleton />
        ) : items.length ? (
          <div className="home-strip">
            {items.map((l) => (
              <ListingTile key={l.id} listing={l} groupLink={offersLink} />
            ))}
          </div>
        ) : (
          <p className="muted">{t("home.sealedEmpty")}</p>
        )}
      </div>
    </section>
  );
}
